/* eslint-disable react/display-name */
import React, { memo, useCallback } from 'react';
import styled from 'styled-components';

import { reduxConnect } from '../../../common';
import { translate } from '../../../common/translation/translate';
import { getDominantPollutant, getGaugeColor, getGaugeFill } from './aqiUtils';
import { HealthAdviceIcon } from './healthAdviceIcon';
import { TopTriggerIcon } from './topTriggerIcons';
import type { HealthAdviceTypes, PollutantType } from './types';
import type { IStoreState, Pollutants } from '../../common/interfaces/types';
import { useAirQualityProvider } from '../../../hooks/useAirQualityProvider';
interface AqiHealthAdviceTopTriggerModalHeaderProps {
    selectedAqiHealthAdvice: keyof typeof HealthAdviceTypes;
    selectedAqiPollutant: keyof typeof PollutantType;
    aqiHealthAdviceTopTriggerPopupHeaderText: string;
    pollutants: Pollutants;
    authId: string;
    secret: string;
    aqiBaseUrl: string;
}
const mapStoreStateToProps = (storeState: IStoreState) => {
    const { airQuality, aqiTopTriggerHealthAdviceDetailModal, appState } = storeState ?? {};
    const { authId, secret, aqiBaseUrl } = appState?.appState || {};
    const { selectedAqiHealthAdvice, selectedAqiPollutant } = aqiTopTriggerHealthAdviceDetailModal ?? {};
    const { currentAirQuality } = airQuality ?? {};

    const { pollutants } = currentAirQuality ?? {};

    return {
        selectedAqiHealthAdvice,
        selectedAqiPollutant,
        pollutants,
        authId,
        secret,
        aqiBaseUrl
    };
};

const mapDispatchToProps = {};

export const AqiHealthAdviceTopTriggerModalHeader = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(memo(({
    selectedAqiPollutant,
    selectedAqiHealthAdvice,
    pollutants,
    authId,
    secret,
    aqiBaseUrl }: AqiHealthAdviceTopTriggerModalHeaderProps) => {

    const { pollutantTypes } = useAirQualityProvider(aqiBaseUrl, authId, secret);

    const renderHeaderIcon = useCallback((healthAdvicetype?: keyof typeof HealthAdviceTypes, pollutantType?: keyof typeof PollutantType) => {
        if (healthAdvicetype) {
            return <HealthAdviceIcon healthAdviceName={healthAdvicetype} iconFillColor='#ffffff' />;
        }
        if (pollutantType) {
            const gaugeFill = getGaugeFill(pollutantTypes, pollutantType, pollutants[pollutantType]?.concentration);
            const gaugeColor = getGaugeColor(gaugeFill);
            return <TopTriggerIcon gaugeColor={gaugeColor} pollutantIconHeight={14} pollutantIconWidth={14} />;
        }
        return null; // should never get here
    }, []);

    const getHeaderText = useCallback((
        healthAdvicetype?: keyof typeof HealthAdviceTypes,
        pollutantType?: keyof typeof PollutantType,
        pollutantsAtLocation?: Pollutants) => {
        if (healthAdvicetype) {
            return translate(healthAdvicetype);
        }
        if (pollutantType && pollutants) {
            const pollutant = pollutantsAtLocation?.[pollutantType];
            const pollutantTitle = getDominantPollutant(pollutantType) ?? '';
            const pollutantConcentrationDisplayText = pollutant?.units && Number.isFinite(pollutant?.concentration)
                ? `${pollutant?.concentration} ${pollutant?.units}`
                : '';
            return `${pollutantTitle} | ${pollutantConcentrationDisplayText}`;
        }
        return ''; // should never get here
    }, []);

    return (
        <Container>
            <IconContainer isHealthAdvicePopup={!!selectedAqiHealthAdvice}>
                {renderHeaderIcon(selectedAqiHealthAdvice, selectedAqiPollutant)}
            </IconContainer>
            <PopupHeaderText>{getHeaderText(selectedAqiHealthAdvice, selectedAqiPollutant, pollutants)}</PopupHeaderText>
        </Container>
    );
}));

AqiHealthAdviceTopTriggerModalHeader.displayName = 'AqiHealthAdviceTopTriggerModalHeader';

const Container = styled.div`
    display: flex;
    flex-direction: row;
    padding: 15px 0 9px 15px;
    margin: 0;
    color: #555555;
    background-color: #FFFFFF;
    font-family: Source Sans Pro;
    font-size: 20px;
    font-weight: 600;
    width: 100%;
`;

interface IconContainerProps {
    isHealthAdvicePopup?: boolean;
}

const IconContainer = styled.div<IconContainerProps>`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    padding: 4px;

    width: 24px;
    height: 24px;
    ${({ isHealthAdvicePopup }) => isHealthAdvicePopup && `
        width: 32px;
        height: 32px;
        background: #8E8E93;
        border-radius: 100px;
    `}
`;

const PopupHeaderText = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: calc(100% - 49px);
    height: 100%;
    font-family: Source Sans Pro;
    font-style: normal;
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    color: #333333;
    padding: 2px 0 0 15px;
`;
